import React, { useCallback, useEffect, useRef, useState } from 'react';

import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

import ConfirmModal from '~/components/General/ConfirmModal';
import { ModalTrashIcon } from '~/assets/images';

import { deleteSavedFilter, listSavedFilters } from '../api';

import './styles.scss';

const SavedFiltersDropdown = ({
  module,
  subModule,
  activeSavedFilterId = null,
  onSelect,
  onDeleted,
  onError,
}) => {
  const triggerRef = useRef(null);
  const menuPaperRef = useRef(null);
  const menuListRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    savedFilter: null,
    loading: false,
  });

  const isOpen = Boolean(anchorEl);

  const fetchSavedFilters = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listSavedFilters({ module, subModule });
      setSavedFilters(response?.data?.data || []);
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Could not load saved filters.');
      setSavedFilters([]);
    } finally {
      setIsLoading(false);
    }
  }, [module, onError, subModule]);

  useEffect(() => {
    if (!isOpen) return;
    fetchSavedFilters();
  }, [fetchSavedFilters, isOpen]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const anchorRect = triggerRef.current?.getBoundingClientRect();

    const handleOutsideScroll = (event) => {
      const scrollTarget = event.target;

      if (
        scrollTarget?.nodeType &&
        (menuPaperRef.current?.contains(scrollTarget) ||
          menuListRef.current?.contains(scrollTarget))
      ) {
        return;
      }

      handleClose();
    };

    const watchAnchorPosition = () => {
      const nextAnchorRect = triggerRef.current?.getBoundingClientRect();

      if (
        anchorRect &&
        nextAnchorRect &&
        (Math.abs(nextAnchorRect.top - anchorRect.top) > 0.5 ||
          Math.abs(nextAnchorRect.left - anchorRect.left) > 0.5)
      ) {
        handleClose();
      }
    };

    document.addEventListener('scroll', handleOutsideScroll, true);
    window.addEventListener('scroll', handleOutsideScroll, true);
    window.addEventListener('wheel', handleOutsideScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener('touchmove', handleOutsideScroll, {
      capture: true,
      passive: true,
    });
    const positionCheckId = window.setInterval(watchAnchorPosition, 25);

    return () => {
      window.clearInterval(positionCheckId);
      document.removeEventListener('scroll', handleOutsideScroll, true);
      window.removeEventListener('scroll', handleOutsideScroll, true);
      window.removeEventListener('wheel', handleOutsideScroll, true);
      window.removeEventListener('touchmove', handleOutsideScroll, true);
    };
  }, [handleClose, isOpen]);

  const handleSelect = (savedFilter) => {
    onSelect?.(savedFilter);
    handleClose();
  };

  const openDeleteConfirm = (event, savedFilter) => {
    event.stopPropagation();
    handleClose();
    setDeleteConfirmModal({
      open: true,
      savedFilter,
      loading: false,
    });
  };

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmModal({
      open: false,
      savedFilter: null,
      loading: false,
    });
  }, []);

  const confirmDelete = useCallback(async () => {
    const savedFilter = deleteConfirmModal.savedFilter;
    if (!savedFilter?.id) return;

    setDeleteConfirmModal((current) => ({ ...current, loading: true }));
    try {
      await deleteSavedFilter(savedFilter.id);
      setSavedFilters((current) => current.filter((item) => item.id !== savedFilter.id));
      onDeleted?.(savedFilter);
      closeDeleteConfirm();
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Could not delete saved filter.');
      setDeleteConfirmModal((current) => ({ ...current, loading: false }));
    }
  }, [closeDeleteConfirm, deleteConfirmModal.savedFilter, onDeleted, onError]);

  const deleteConfirmModalConfig = {
    open: deleteConfirmModal.open,
    header: 'Delete saved filter',
    message: 'Are you sure you want to delete this saved filter?',
    handleOk: confirmDelete,
    handleClose: closeDeleteConfirm,
    icon: ModalTrashIcon,
    loading: deleteConfirmModal.loading,
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    className: 'savedFilterDeleteConfirmModal',
  };

  return (
    <>
      <button
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Saved filters"
        className={`savedFiltersBookmarkButton${isOpen ? ' active' : ''}`}
        onClick={handleOpen}
        type="button"
      >
        <BookmarkBorderIcon />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        className="savedFiltersMenu"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{
          ref: menuListRef,
        }}
        PaperProps={{
          ref: menuPaperRef,
        }}
      >
        {isLoading ? (
          <Box className="savedFiltersMenuState">
            <CircularProgress className="savedFiltersMenuLoader" size={20} />
          </Box>
        ) : savedFilters.length ? (
          savedFilters.map((savedFilter) => (
            <MenuItem
              key={savedFilter.id}
              className={`savedFiltersMenuItem${activeSavedFilterId === savedFilter.id ? ' active' : ''}`}
              onClick={() => handleSelect(savedFilter)}
            >
              <Typography className="savedFiltersMenuItemLabel">{savedFilter.name}</Typography>
              <IconButton
                aria-label={`Delete ${savedFilter.name}`}
                className="savedFiltersDeleteButton"
                onClick={(event) => openDeleteConfirm(event, savedFilter)}
                size="small"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        ) : (
          <Box className="savedFiltersMenuState">
            <Typography>No saved filters</Typography>
          </Box>
        )}
      </Menu>

      <ConfirmModal openConfirmModal={deleteConfirmModalConfig} />
    </>
  );
};

export default SavedFiltersDropdown;
